import React, { ReactNode, useEffect, useState } from 'react';
import css from './ExistingPatient.module.css';
import {
  Demographics,
  EmptyDemographics,
  HaveMRN,
  PatientDetails,
  PreviouslySeen,
  SchedulingTarget,
} from '../../../../../../api/models';
import {
  DateInputField,
  Paragraph,
  RadioCardGroup,
  RadioCardOption,
  RadioCardSizing,
  StackedColumn,
  StackedColumnSpacing,
  TextLink,
  TextField,
} from '../../../../../../core';
import { LearnMoreDialog } from './learnMoreDialog';

export interface ExistingPatientProps {
  validate: boolean;
  patientDetails?: PatientDetails;
  onChangePatientDetails: (patientDetails: PatientDetails) => void;
}

const previouslySeenOptions: RadioCardOption[] = [
  {
    value: PreviouslySeen.Yes,
    label: PreviouslySeen.Yes,
  },
  {
    value: PreviouslySeen.No,
    label: PreviouslySeen.No,
  },
  {
    value: PreviouslySeen.NotSure,
    label: PreviouslySeen.NotSure,
  },
];

const ExistingPatient: React.FC<ExistingPatientProps> = (
  props: ExistingPatientProps
) => {
  const [previouslySeen, setPreviouslySeen] = useState<PreviouslySeen>();
  const [knowMRN, setKnowMRN] = useState<HaveMRN>();
  const [demographics, setDemographics] = useState<Demographics>(
    props.patientDetails?.demographics ?? EmptyDemographics
  );
  const [showLearnMoreModal, setShowLearnMoreModal] = useState<boolean>(false);

  useEffect(() => {
    setPreviouslySeen(props.patientDetails?.previouslySeen);
    setKnowMRN(props.patientDetails?.haveMRN);
    setDemographics(props.patientDetails?.demographics ?? EmptyDemographics);
  }, [props.patientDetails]);

  const onChangePreviouslySeen = (selectedValue: string) => {
    const selectedResponse = selectedValue as PreviouslySeen;

    if (selectedResponse) {
      setPreviouslySeen(selectedResponse);
      props.onChangePatientDetails({
        ...props.patientDetails,
        previouslySeen: selectedResponse,
      });
    }
  };

  const onChangeKnowMRN = (selectedValue: string) => {
    const selectedResponse = selectedValue as HaveMRN;

    if (selectedResponse) {
      setKnowMRN(selectedResponse);
      props.onChangePatientDetails({
        ...props.patientDetails,
        haveMRN: selectedResponse,
      });
    }
  };

  const onLearnMoreClicked = () => {
    setShowLearnMoreModal(true);
  };

  const onCloseLearnMoreModal = () => {
    setShowLearnMoreModal(false);
  };

  const yourText =
    props.patientDetails?.target === SchedulingTarget.Myself ? 'your' : 'their';
  const myText =
    props.patientDetails?.target === SchedulingTarget.Myself ? 'my' : 'their';

  const haveMRNOptions: RadioCardOption[] = [
    {
      value: HaveMRN.Yes,
      label: `I have ${myText} Medical Record Number (MRN)`,
    },
    {
      value: HaveMRN.No,
      label: `I don't know ${myText} Medical Record Number (MRN)`,
    },
  ];

  const mrnLearnMore = (
    <>
      <Paragraph
        small={true}
        text={`Streamline your experience by providing ${yourText} Medical Record Number (MRN).`}
      />
      <TextLink
        captionClassName={css.learnMoreLink}
        caption="Learn more about where to find your MRN. "
        onClick={onLearnMoreClicked}
      />
    </>
  );

  const onMRNChanged = (value: string) => {
    const newDemographics = { ...demographics, mrn: value };
    setDemographics(newDemographics);
    props.onChangePatientDetails({
      ...props.patientDetails,
      demographics: newDemographics,
    });
  };

  const onDOBChanged = (value: string) => {
    const newDemographics = { ...demographics, dateOfBirth: value };
    setDemographics(newDemographics);
    props.onChangePatientDetails({
      ...props.patientDetails,
      demographics: newDemographics,
    });
  };

  const verifyMRN: ReactNode =
    previouslySeen === PreviouslySeen.Yes && knowMRN === HaveMRN.Yes ? (
      <StackedColumn spacing={StackedColumnSpacing.Wider}>
        <div />
        <StackedColumn spacing={StackedColumnSpacing.Wide}>
          <TextField
            value={demographics.mrn ?? ''}
            label="Medical Record Number (MRN)"
            required={true}
            validate={props.validate}
            onChange={onMRNChanged}
          />
          <DateInputField
            value={demographics.dateOfBirth}
            label="Date of birth (MM/DD/YYYY)"
            required={true}
            validate={props.validate}
            onChange={onDOBChanged}
          />
        </StackedColumn>
        <div />
      </StackedColumn>
    ) : undefined;

  const haveMrnIndex = haveMRNOptions.findIndex(
    (option) => option.value === HaveMRN.Yes
  );
  haveMRNOptions[haveMrnIndex] = {
    ...haveMRNOptions[haveMrnIndex],
    associatedChild: verifyMRN,
  };

  return (
    <>
      <RadioCardGroup
        label={`${
          props.patientDetails?.target === SchedulingTarget.Myself
            ? 'Have you'
            : 'Has the patient'
        } been seen at the Marshfield Clinic Health System before?`}
        required={true}
        validate={props.validate}
        groupName="previouslySeen"
        sizing={RadioCardSizing.Small}
        fullWidth={true}
        radioOptions={previouslySeenOptions}
        selectedValue={previouslySeen ?? ''}
        onChange={onChangePreviouslySeen}
      />
      {previouslySeen === PreviouslySeen.Yes ? (
        <RadioCardGroup
          label={`Do you know ${
            props.patientDetails?.target === SchedulingTarget.Myself
              ? 'your'
              : "the patient's"
          } Medical Record Number (MRN)?`}
          required={true}
          validate={props.validate}
          optionalNode={mrnLearnMore}
          groupName="knowMRN"
          sizing={RadioCardSizing.Small}
          fullWidth={true}
          radioOptions={haveMRNOptions}
          selectedValue={knowMRN ?? ''}
          onChange={onChangeKnowMRN}
        />
      ) : null}
      {showLearnMoreModal ? (
        <LearnMoreDialog isOpen={showLearnMoreModal} onClose={onCloseLearnMoreModal} />
      ) : null}
    </>
  );
};

export default ExistingPatient;
